import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./dashboard.css";
import "./editTeachersData.css";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faSearch,
  faFilter,
  faChalkboardTeacher,
  faIdCard,
  faPhone,
  faAddressCard,
  faGraduationCap,
  faSpinner,
  faDatabase,
  faUserCheck,
  faBriefcase,
  faBuilding,
  faExclamationTriangle,
  faChartLine,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import AsyncStorage from "./utils/asyncStorage";

// Your PHP backend base URL
const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api/TeacherCrud.php";

const FetchTeachers = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);
  const [teachers, setTeachers] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 1,
  });

  const [filters, setFilters] = useState({
    useTeacherId: false,
    useTeacherMobile: false,
    useTeacherCNIC: false,
    teacher_id: "",
    teacher_mobile: "",
    teacher_cnic: "",
  });

  const [teacherIdSuggestions, setTeacherIdSuggestions] = useState([]);
  const [teacherMobileSuggestions, setTeacherMobileSuggestions] = useState([]);
  const [teacherCNICSuggestions, setTeacherCNICSuggestions] = useState([]);

  // Calculate active filters
  useEffect(() => {
    const activeCount = [
      filters.useTeacherId && filters.teacher_id,
      filters.useTeacherMobile && filters.teacher_mobile,
      filters.useTeacherCNIC && filters.teacher_cnic,
    ].filter(Boolean).length;
    setActiveFilters(activeCount);
  }, [filters]);

  // Load all teachers on component mount
  useEffect(() => {
    fetchTeachers();
  }, []);

  // Debounced suggestion fetching for Teacher ID
  const fetchTeacherIdSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length >= 2 && filters.useTeacherId) {
        try {
          const response = await axios.get(API_BASE_URL, {
            params: {
              action: "search-teacher-id",
              query: query,
            },
          });

          if (response.data && response.data.suggestions) {
            setTeacherIdSuggestions(response.data.suggestions || []);
          }
        } catch (error) {
          console.error("Error fetching teacher ID suggestions:", error);
          setTeacherIdSuggestions([]);
        }
      } else {
        setTeacherIdSuggestions([]);
      }
    }, 300),
    [filters.useTeacherId]
  );

  // Debounced suggestion fetching for Teacher Mobile
  const fetchTeacherMobileSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length >= 2 && filters.useTeacherMobile) {
        try {
          const response = await axios.get(API_BASE_URL, {
            params: {
              action: "search-teacher-mobile",
              query: query,
            },
          });

          if (response.data && response.data.suggestions) {
            setTeacherMobileSuggestions(response.data.suggestions || []);
          }
        } catch (error) {
          console.error("Error fetching teacher mobile suggestions:", error);
          setTeacherMobileSuggestions([]);
        }
      } else {
        setTeacherMobileSuggestions([]);
      }
    }, 300),
    [filters.useTeacherMobile]
  );

  // Debounced suggestion fetching for Teacher CNIC
  const fetchTeacherCNICSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length >= 2 && filters.useTeacherCNIC) {
        try {
          const response = await axios.get(API_BASE_URL, {
            params: {
              action: "search-teacher-cnic",
              query: query,
            },
          });

          if (response.data && response.data.suggestions) {
            setTeacherCNICSuggestions(response.data.suggestions || []);
          }
        } catch (error) {
          console.error("Error fetching teacher CNIC suggestions:", error);
          setTeacherCNICSuggestions([]);
        }
      } else {
        setTeacherCNICSuggestions([]);
      }
    }, 300),
    [filters.useTeacherCNIC]
  );

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });

    if (name === "teacher_id" && filters.useTeacherId) {
      fetchTeacherIdSuggestions(value);
    }
    if (name === "teacher_mobile" && filters.useTeacherMobile) {
      fetchTeacherMobileSuggestions(value);
    }
    if (name === "teacher_cnic" && filters.useTeacherCNIC) {
      fetchTeacherCNICSuggestions(value);
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFilters({ ...filters, [name]: checked });

    // Clear suggestions when filter is disabled
    if (name === "useTeacherId" && !checked) {
      setTeacherIdSuggestions([]);
      setFilters((prev) => ({ ...prev, teacher_id: "" }));
    }
    if (name === "useTeacherMobile" && !checked) {
      setTeacherMobileSuggestions([]);
      setFilters((prev) => ({ ...prev, teacher_mobile: "" }));
    }
    if (name === "useTeacherCNIC" && !checked) {
      setTeacherCNICSuggestions([]);
      setFilters((prev) => ({ ...prev, teacher_cnic: "" }));
    }
  };

  const handleSuggestionClick = (suggestion, type) => {
    if (typeof suggestion === "object") {
      // Handle object suggestions (from API)
      if (suggestion.teacher_id) {
        setFilters({ ...filters, teacher_id: suggestion.teacher_id });
      } else if (suggestion.primary_mobile) {
        setFilters({ ...filters, teacher_mobile: suggestion.primary_mobile });
      } else if (suggestion.cnic) {
        setFilters({ ...filters, teacher_cnic: suggestion.cnic });
      }
    } else {
      // Handle string suggestions
      setFilters({ ...filters, [type]: suggestion });
    }

    // Clear suggestions
    if (type === "teacher_id") setTeacherIdSuggestions([]);
    if (type === "teacher_mobile") setTeacherMobileSuggestions([]);
    if (type === "teacher_cnic") setTeacherCNICSuggestions([]);
  };

  // Fetch teachers based on filters
  const fetchTeachers = async () => {
    setIsLoading(true);
    try {
      console.log("Fetching teachers with filters:", filters);

      const params = {
        action: "fetch-teachers",
        useTeacherId: filters.useTeacherId,
        useTeacherMobile: filters.useTeacherMobile,
        useTeacherCNIC: filters.useTeacherCNIC,
      };

      // Only add filter values if the checkbox is checked and value exists
      if (filters.useTeacherId && filters.teacher_id) {
        params.teacher_id = filters.teacher_id;
      }
      if (filters.useTeacherMobile && filters.teacher_mobile) {
        params.teacher_mobile = filters.teacher_mobile;
      }
      if (filters.useTeacherCNIC && filters.teacher_cnic) {
        params.teacher_cnic = filters.teacher_cnic;
      }

      // Add pagination
      params.page = pagination.page;
      params.limit = pagination.limit;

      const response = await axios.get(API_BASE_URL, { params });

      console.log("API Response:", response.data);

      if (response.data.success) {
        setTeachers(response.data.data || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }

        if (response.data.data && response.data.data.length === 0) {
          Swal.fire({
            icon: "info",
            title: "No Results Found",
            text: "No teachers match your search criteria.",
            background: "#f8fafc",
            color: "#2d3748",
            confirmButtonColor: "#667eea",
          });
        }
      } else {
        throw new Error(response.data.error || "Failed to fetch teachers");
      }
    } catch (error) {
      console.error("Error fetching teachers data:", error.response || error);

      let errorMessage = "Failed to fetch teachers. Please try again.";
      if (error.response) {
        errorMessage = error.response.data?.error || errorMessage;
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        background: "#f8fafc",
        color: "#2d3748",
        confirmButtonColor: "#667eea",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Edit action - Store in Async Storage and navigate
  const handleEdit = async (teacher) => {
    try {
      // Store teacher data in Async Storage
      await AsyncStorage.setItem("currentEditingTeacher", teacher);
      console.log("Teacher data stored in AsyncStorage:", teacher);

      // Navigate to edit panel
      navigate("/Edit-Teacher-Panel", {
        state: {
          id: teacher.id,
          fromStorage: true,
        },
      });

      // Show success message
      Swal.fire({
        icon: "success",
        title: "Loading Teacher Data",
        text: "Teacher data loaded for editing.",
        timer: 2000,
        showConfirmButton: false,
        background: "#f8fafc",
        color: "#2d3748",
      });
    } catch (error) {
      console.error("Error storing teacher data:", error);
      // Fallback to navigate without storage
      navigate("/Edit-Teacher-Panel", { state: { id: teacher.id } });
    }
  };

  // Handle Delete action - UPDATED WITH PHP IMPLEMENTATION
  const handleDelete = async (teacherId, teacherName, teacherTeacherId) => {
    const result = await Swal.fire({
      title: "Delete Teacher",
      html: `
        <div style="text-align: center; padding: 1rem;">
          <div style="font-size: 4rem; color: #f56565; margin-bottom: 1rem;">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <h3 style="color: #2d3748; margin-bottom: 0.5rem;">Delete Teacher?</h3>
          <p style="color: #718096;">
            Are you sure you want to delete <strong>${teacherName}</strong> (ID: ${teacherTeacherId})?
            <br><br>
            <strong style="color: #f56565;">⚠️ This action cannot be undone!</strong>
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f56565",
      cancelButtonColor: "#cbd5e0",
      confirmButtonText: "Yes, Delete Permanently",
      cancelButtonText: "Cancel",
      background: "#f8fafc",
      color: "#2d3748",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          // Make DELETE request to PHP backend
          const response = await axios({
            method: "POST",
            url: API_BASE_URL,
            data: new URLSearchParams({
              action: "delete-teacher",
              id: teacherId,
            }),
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          });

          if (!response.data.success) {
            throw new Error(response.data.error || "Failed to delete teacher");
          }

          return response.data;
        } catch (error) {
          let errorMessage = "Failed to delete teacher.";
          if (error.response && error.response.data) {
            errorMessage = error.response.data.error || errorMessage;
          } else if (error.message) {
            errorMessage = error.message;
          }
          throw new Error(errorMessage);
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (result.isConfirmed) {
      // Remove the deleted teacher from the local state
      setTeachers((prevTeachers) =>
        prevTeachers.filter((teacher) => teacher.id !== teacherId)
      );

      // Update total count
      setPagination((prev) => ({
        ...prev,
        total: prev.total - 1,
      }));

      Swal.fire({
        icon: "success",
        title: "Teacher Deleted",
        html: `
          <div style="text-align: center; padding: 1rem;">
            <div style="font-size: 3rem; color: #38a169; margin-bottom: 1rem;">
              <i class="fas fa-check-circle"></i>
            </div>
            <h3 style="color: #2d3748; margin-bottom: 0.5rem;">Successfully Deleted</h3>
            <p style="color: #718096;">
              Teacher <strong>${teacherName}</strong> (ID: ${teacherTeacherId}) has been permanently deleted.
            </p>
          </div>
        `,
        confirmButtonColor: "#667eea",
        background: "#f8fafc",
        color: "#2d3748",
      });

      // Refresh the list if we're on the last page and it becomes empty
      if (teachers.length === 1 && pagination.page > 1) {
        setPagination((prev) => ({
          ...prev,
          page: prev.page - 1,
        }));
        fetchTeachers();
      }
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      useTeacherId: false,
      useTeacherMobile: false,
      useTeacherCNIC: false,
      teacher_id: "",
      teacher_mobile: "",
      teacher_cnic: "",
    });
    setTeacherIdSuggestions([]);
    setTeacherMobileSuggestions([]);
    setTeacherCNICSuggestions([]);
  };

  // Function to clear AsyncStorage (for testing/development)
  const clearStorage = async () => {
    await AsyncStorage.clear();
    Swal.fire({
      icon: "info",
      title: "Storage Cleared",
      text: "All locally stored data has been cleared.",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination({ ...pagination, page: newPage });
    }
  };

  // Load teachers when pagination changes
  useEffect(() => {
    fetchTeachers();
  }, [pagination.page]);

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="fetch-teachers-container">
        {/* Header Section */}
        <div className="dashboard-header">
          <div>
            <h2>
              <FontAwesomeIcon icon={faChalkboardTeacher} />
              Teacher Management
            </h2>
            <p
              style={{
                color: "#718096",
                marginTop: "0.5rem",
                fontSize: "0.95rem",
              }}
            >
              Search, filter, and manage teacher records
            </p>
          </div>
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <button
              onClick={clearStorage}
              className="search-button"
              style={{
                background: "linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)",
                fontSize: "0.9rem",
                padding: "0.5rem 1rem",
              }}
              title="Clear Local Storage"
            >
              <FontAwesomeIcon icon={faSync} />
              Clear Storage
            </button>
            <button
              onClick={fetchTeachers}
              className="search-button"
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faSearch} />
              Search Teachers
              {isLoading && (
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  style={{ marginLeft: "0.5rem" }}
                />
              )}
            </button>
          </div>
        </div>

        {/* Filters Card */}
        <div className="filters-card">
          <h3>
            <FontAwesomeIcon icon={faFilter} />
            Search Filters
            {activeFilters > 0 && (
              <span className="stats-badge">
                {activeFilters} active filter{activeFilters !== 1 ? "s" : ""}
              </span>
            )}
          </h3>

          <div className="filters-grid">
            {/* Teacher ID Filter */}
            <div className="filter-group">
              <label className="filter-label">
                <span className="checkbox-container">
                  <input
                    type="checkbox"
                    name="useTeacherId"
                    checked={filters.useTeacherId}
                    onChange={handleCheckboxChange}
                    className="custom-checkbox"
                  />
                </span>
                <FontAwesomeIcon icon={faIdCard} />
                Teacher ID
              </label>
              <input
                type="text"
                name="teacher_id"
                value={filters.teacher_id}
                onChange={handleInputChange}
                placeholder="Enter teacher ID (e.g., Prof-2501)"
                disabled={!filters.useTeacherId}
                className="filter-input"
              />
              {teacherIdSuggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {teacherIdSuggestions.map((idObj, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(idObj, "teacher_id")}
                    >
                      {idObj.teacher_id || idObj}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Teacher Mobile Filter */}
            <div className="filter-group">
              <label className="filter-label">
                <span className="checkbox-container">
                  <input
                    type="checkbox"
                    name="useTeacherMobile"
                    checked={filters.useTeacherMobile}
                    onChange={handleCheckboxChange}
                    className="custom-checkbox"
                  />
                </span>
                <FontAwesomeIcon icon={faPhone} />
                Mobile Number
              </label>
              <input
                type="text"
                name="teacher_mobile"
                value={filters.teacher_mobile}
                onChange={handleInputChange}
                placeholder="Enter mobile number (e.g., 0300-1234567)"
                disabled={!filters.useTeacherMobile}
                className="filter-input"
              />
              {teacherMobileSuggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {teacherMobileSuggestions.map((mobileObj, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onClick={() =>
                        handleSuggestionClick(mobileObj, "teacher_mobile")
                      }
                    >
                      {mobileObj.primary_mobile || mobileObj}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Teacher CNIC Filter */}
            <div className="filter-group">
              <label className="filter-label">
                <span className="checkbox-container">
                  <input
                    type="checkbox"
                    name="useTeacherCNIC"
                    checked={filters.useTeacherCNIC}
                    onChange={handleCheckboxChange}
                    className="custom-checkbox"
                  />
                </span>
                <FontAwesomeIcon icon={faAddressCard} />
                CNIC Number
              </label>
              <input
                type="text"
                name="teacher_cnic"
                value={filters.teacher_cnic}
                onChange={handleInputChange}
                placeholder="Enter CNIC (e.g., 35202-1234567-1)"
                disabled={!filters.useTeacherCNIC}
                className="filter-input"
              />
              {teacherCNICSuggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {teacherCNICSuggestions.map((cnicObj, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onClick={() =>
                        handleSuggestionClick(cnicObj, "teacher_cnic")
                      }
                    >
                      {cnicObj.cnic || cnicObj}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: "0", marginTop: "1.5rem" }}>
            <button
              onClick={clearFilters}
              className="search-button"
              style={{
                background: "linear-gradient(135deg, #718096 0%, #4a5568 100%)",
              }}
            >
              <FontAwesomeIcon icon={faFilter} />
              Clear Filters
            </button>
          </div>
        </div>

        {/* Results Section */}
        {isLoading ? (
          <div className="loader">
            <div className="loader-spinner"></div>
            <div
              style={{
                marginLeft: "1rem",
                color: "#4a5568",
                fontSize: "1.1rem",
              }}
            >
              Fetching teacher data...
            </div>
          </div>
        ) : teachers.length > 0 ? (
          <>
            <div className="table-container">
              <div className="table-header">
                <div>
                  <h3>
                    <FontAwesomeIcon icon={faChalkboardTeacher} />
                    Teachers List
                  </h3>
                  <p
                    style={{
                      color: "#718096",
                      fontSize: "0.9rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    Click on action buttons to edit or delete teacher records
                  </p>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "1rem" }}
                >
                  <span className="stats-badge">
                    <FontAwesomeIcon icon={faUserCheck} />
                    {pagination.total} teacher
                    {pagination.total !== 1 ? "s" : ""} total
                  </span>
                  <span className="stats-badge">
                    Showing {(pagination.page - 1) * pagination.limit + 1}-
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}{" "}
                    of {pagination.total}
                  </span>
                </div>
              </div>

              <div className="table-wrapper">
                <table className="teacher-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Teacher Details</th>
                      <th>Contact</th>
                      <th>Education</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.map((teacher) => (
                      <tr key={teacher.id}>
                        <td style={{ width: "8rem" }}>
                          <span className="teacher-id">
                            {teacher.teacher_id}
                          </span>
                        </td>
                        <td className="teacher-name-cell">
                          <span className="teacher-name-primary">
                            {teacher.name}
                          </span>
                          <span className="teacher-details-secondary">
                            <strong>Father:</strong> {teacher.father_name} •
                            <strong> CNIC:</strong> {teacher.cnic}
                          </span>
                        </td>
                        <td>
                          <div>
                            <div className="contact-number">
                              {teacher.primary_mobile}
                            </div>
                            <div className="teacher-email">{teacher.gmail}</div>
                            <div
                              style={{
                                fontSize: "0.85rem",
                                color: "#718096",
                                marginTop: "0.25rem",
                              }}
                            >
                              <strong>Gender:</strong> {teacher.gender}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div
                              style={{ fontWeight: "600", color: "#2d3748" }}
                            >
                              {teacher.highest_education}
                            </div>
                            <div
                              style={{
                                fontSize: "0.85rem",
                                color: "#718096",
                                marginTop: "0.25rem",
                              }}
                            >
                              {teacher.grade_or_cgpa || "Grade not specified"}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => handleEdit(teacher)}
                              className="action-btn edit-btn"
                              title={`Edit ${teacher.name}`}
                            >
                              <FontAwesomeIcon icon={faEdit} color="#4299e1" />
                            </button>
                            <button
                              onClick={() =>
                                handleDelete(
                                  teacher.id,
                                  teacher.name,
                                  teacher.teacher_id
                                )
                              }
                              className="action-btn delete-btn"
                              title={`Delete ${teacher.name}`}
                            >
                              <FontAwesomeIcon icon={faTrash} color="#f56565" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className="pagination-controls">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="pagination-button"
                  >
                    Previous
                  </button>

                  <div className="pagination-info">
                    Page {pagination.page} of {pagination.totalPages}
                  </div>

                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="pagination-button"
                  >
                    Next
                  </button>
                </div>
              )}

              {/* Table Footer */}
              <div
                style={{
                  marginTop: "2rem",
                  paddingTop: "1.5rem",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: "#718096",
                  fontSize: "0.9rem",
                }}
              >
                <div>
                  Showing{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total
                  )}{" "}
                  of {pagination.total} records
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  Click the search button to refresh data
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="table-container">
            <div className="empty-state">
              <FontAwesomeIcon icon={faDatabase} className="empty-state-icon" />
              <h3>No Teachers Found</h3>
              <p>
                Try adjusting your search criteria or click below to load all
                teachers
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "center",
                  marginTop: "1.5rem",
                }}
              >
                <button
                  onClick={clearFilters}
                  className="search-button"
                  style={{
                    background:
                      "linear-gradient(135deg, #718096 0%, #4a5568 100%)",
                    minWidth: "180px",
                  }}
                >
                  <FontAwesomeIcon icon={faFilter} />
                  Clear Filters
                </button>
                <button
                  onClick={fetchTeachers}
                  className="search-button"
                  style={{
                    minWidth: "180px",
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                  Load All Teachers
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FetchTeachers;
